import { Mesh, Vector2, Shape, ShapeGeometry, Vector3, BackSide, FrontSide, MeshStandardMaterial } from 'three';
import { Point, Utils, Fn } from './utils';
import { Corner } from './corner';
import { Floorplan } from './floorplan';
import { HalfEdge } from './half_edge';
import { RoomDTO } from 'src/app/models/product-models/DTOs/RoomDTO';
import { CoveringDTO } from 'src/app/models/product-models/DTOs/CoveringDTO';
import { TextureDataDTO } from 'src/app/models/product-models/DTOs/TextureDataDTO';
import { CoveringType } from 'src/app/models/interfaces/GenericInterfaces';

export class Room {
  private _floorTexture = new CoveringDTO(
    CoveringType.Texture,
    new TextureDataDTO(
      'assets/textures/materials/wood3_color.webp',
      undefined,
      undefined,
      'assets/textures/materials/wood3_normal.webp',
      0.6,
      0.5,
      undefined,
      undefined,
      0.2,
      [1, 1],
    ),
    'assets/textures/thumbnails/wood3_thumb.webp',
  );

  private _ceilingTexture = new CoveringDTO(
    CoveringType.Texture,
    new TextureDataDTO(
      'assets/textures/materials/wall.webp',
      undefined,
      undefined,
      'assets/textures/materials/wall_normal.webp',
      0.7,
      0.3,
      undefined,
      undefined,
      undefined,
      [10, 10],
    ),
  );

  private _floorImage: HTMLImageElement | undefined = undefined;

  private _ceilingImage: HTMLImageElement | undefined = undefined;

  /** */
  public interiorCorners: Point[] = [];

  /** */
  private _edgePointer: HalfEdge | null = null;

  /** floor plane for intersection testing */
  public floorPlane: Mesh | null = null;

  /** ceiling plane */
  public ceilingPlane: Mesh | null = null;

  /** */
  private _floorChangeCallbacks: Fn[] = [];

  /** */
  private _ceilingChangeCallbacks: Fn[] = [];

  /**
   *  ordered CCW
   */
  constructor(
    private floorplan: Floorplan,
    public corners: Corner[],
  ) {
    this.updateWalls();
    this.updateInteriorCorners();

    this.fireOnCeilingChange(() => {
      this.floorplan.fireRedraw();
    });

    this.fireOnFloorChange(() => {
      this.floorplan.fireRedraw();
    });

    this.setCeilingTexture();
    this.setFloorTexture();
    //this.generatePlane();
  }

  public equals(room: Room) {
    if (this.corners.length !== room.corners.length) {
      return false;
    }

    const length = this.corners.length;

    for (let i = 0; i < length; i++) {
      const corner1 = this.corners[i];
      let cornerCheck = false;

      for (let j = 0; j < length; j++) {
        const corner2 = room.corners[j];

        if (corner1.x === corner2.x || corner1.y === corner2.y) {
          cornerCheck = true;
          break;
        }
      }

      if (!cornerCheck) return false;
    }

    return true;
  }

  public toDTO(corners?: Corner[]) {
    let cornerIds: string[] = [];

    this.updateWalls();
    this.updateInteriorCorners();

    if (corners) {
      this.interiorCorners.forEach((interiorCorner) => {
        const foundCorner = corners.find((corner) => corner.x === interiorCorner.x && corner.y === interiorCorner.y);
        if (foundCorner) cornerIds.push(foundCorner.uuid);
      });
    } else {
      cornerIds = this.corners.map((corner) => corner.uuid) as string[];
    }

    return new RoomDTO(this.getUuid(), cornerIds, this._floorTexture, this._ceilingTexture);
  }

  public setCorners(corners: Corner[]) {
    this.corners = corners;
  }

  public getUuid(): string {
    /* const cornerUuids = Utils.map(this.corners, function (c: Corner) {
      return c.uuid;
    });
    cornerUuids.sort();
    return cornerUuids.join(); */
    return Utils.guid();
  }

  public fireOnFloorChange(callback: Fn) {
    this._floorChangeCallbacks.push(callback);
  }

  public fireOnCeilingChange(callback: Fn) {
    this._ceilingChangeCallbacks.push(callback);
  }

  public fireFloorChange() {
    this._floorChangeCallbacks.forEach((callback: Fn) => callback());
  }

  public fireCeilingChange() {
    this._ceilingChangeCallbacks.forEach((callback: Fn) => callback());
  }

  public getFloorTexture() {
    return this._floorTexture;
  }

  public getCeilingTexture() {
    return this._ceilingTexture;
  }

  public getFloorImage() {
    return this._floorImage;
  }

  public getCeilingImage() {
    return this._ceilingImage;
  }

  public setFloorTexture(customTexture?: CoveringDTO) {
    const texture = customTexture || this._floorTexture;
    this._floorTexture = texture;

    if (texture.type === CoveringType.Texture) {
      this._floorImage = new Image();

      this._floorImage.onload = () => {
        this.fireFloorChange();
      };

      this._floorImage.src = texture.textureData.textureUrl;
    } else {
      this._floorImage = undefined;
      this.fireFloorChange();
    }
  }

  public setCeilingTexture(customTexture?: CoveringDTO) {
    const texture = customTexture || this._ceilingTexture;
    this._ceilingTexture = texture;

    if (texture.type === CoveringType.Texture) {
      this._ceilingImage = new Image();

      this._ceilingImage.onload = () => {
        this.fireCeilingChange();
      };

      this._ceilingImage.src = texture.textureData.textureUrl;
    } else {
      this._ceilingImage = undefined;
      this.fireCeilingChange();
    }
  }

  public getRoomSize() {
    const points: Vector2[] = [];

    this.interiorCorners.forEach((corner) => {
      points.push(new Vector2(corner.x, corner.y));
    });

    const shape = new Shape(points);
    const geometry = new ShapeGeometry(shape);
    geometry.computeBoundingBox();

    const size = new Vector3();
    geometry.boundingBox?.getSize(size);

    return size;
  }

  public generateFloorPlane(moveVector: Vector2, material: MeshStandardMaterial) {
    const points: Vector2[] = [];

    this.interiorCorners.forEach((corner) => {
      points.push(new Vector2((corner.x - moveVector.x) / 100, (corner.y - moveVector.y) / 100));
    });

    const shape = new Shape(points);
    const geometry = new ShapeGeometry(shape);
    material.side = BackSide;

    this.floorPlane = new Mesh(geometry, material);

    this.floorPlane.rotation.set(Math.PI / 2, 0, 0);
    return this.floorPlane;
  }

  public generateCeilingPlane(moveVector: Vector2, material: MeshStandardMaterial, height: number) {
    const points: Vector2[] = [];

    this.interiorCorners.forEach((corner) => {
      points.push(new Vector2((corner.x - moveVector.x) / 100, (corner.y - moveVector.y) / 100));
    });

    const shape = new Shape(points);
    const geometry = new ShapeGeometry(shape);
    material.side = FrontSide;

    this.ceilingPlane = new Mesh(geometry, material);
    this.ceilingPlane.position.y = height / 100;

    this.ceilingPlane.rotation.set(Math.PI / 2, 0, 0);
    return this.ceilingPlane;
  }

  private cycleIndex(index: number) {
    if (index < 0) {
      return (index += this.corners.length);
    } else {
      return index % this.corners.length;
    }
  }

  public updateInteriorCorners() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let edge: any = this._edgePointer;

    this.interiorCorners = [];

    if (edge) {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        this.interiorCorners.push(edge.interiorStart());
        //edge.generatePlane();
        if (edge.next === this._edgePointer) {
          break;
        } else {
          edge = edge.next;
        }
      }
    }
  }

  /**
   * Populates each wall's half edge relating to this room
   * this creates a fancy doubly connected edge list (DCEL)
   */
  public updateWalls() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let prevEdge: any = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let firstEdge: any = null;

    for (let i = 0; i < this.corners.length; i++) {
      const firstCorner = this.corners[i];
      const secondCorner = this.corners[(i + 1) % this.corners.length];

      // find if wall is heading in that direction
      const wallTo = firstCorner.wallTo(secondCorner);
      const wallFrom = firstCorner.wallFrom(secondCorner);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let edge: any = null;

      if (wallTo) {
        edge = new HalfEdge(wallTo, true);
      } else if (wallFrom) {
        edge = new HalfEdge(wallFrom, false);
      } else {
        // something horrible has happened
        throw Error('Corners arent connected by a wall, uh oh');
      }

      if (i === 0) {
        firstEdge = edge;
      } else {
        edge.prev = prevEdge;
        prevEdge.next = edge;
        if (i + 1 === this.corners.length) {
          firstEdge.prev = edge;
          edge.next = firstEdge;
        }
      }

      prevEdge = edge;
    }

    // hold on to an edge reference
    this._edgePointer = firstEdge;
  }
}
